import { useCallback, useEffect, useState } from "react";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CircleSlash2,
  Clock,
  RefreshCw,
  Timer,
  Users,
  Zap,
} from "lucide-react";
import type { AgendaItem, WodBlocks } from "@/types";
import { DatePicker } from "@/components/ui/date-picker";
import { AgendaPill } from "@/components/StatusPill";
import {
  clockTime,
  countdown,
  displayDate,
  shiftOpenDay,
  shortDate,
  skipSunday,
  toApiDate,
  todayIso,
  weekdayShort,
} from "@/lib/fmt";

interface ActiveClass {
  idConfiguracao: number;
  nome: string;
  horaInicio: string;
  ocupacao: number;
  capacidade: number;
  selecionado: boolean;
  status: number;
}

interface Participant {
  idCliente: number;
  nome: string;
  foto: string | null;
  oculto: boolean;
}

interface WodResponse {
  date: string;
  wod: WodBlocks | null;
  activity: ActiveClass | null;
  participants: Participant[];
}

export interface NextExecutionInfo {
  type: "prep" | "exec";
  activityName: string;
  /** Class time, e.g. "06:00". */
  time: string;
  /** Class date in the API format (yyyy-M-dd). */
  targetDate: string;
  windowOpensAt: string;
  prepAt: string;
}

function OccupancyMeter({ ocupacao, capacidade }: { ocupacao: number; capacidade: number }) {
  if (!capacidade) return null;
  const pct = Math.min((ocupacao / capacidade) * 100, 100);
  const color = pct >= 90 ? "var(--cb-error)" : pct >= 70 ? "var(--cb-warning)" : "var(--cb-success)";
  return (
    <div style={{ display: "grid", gap: "0.35rem" }}>
      <div className="cb-meter">
        <div className="cb-meter__fill" style={{ background: color, width: `${pct}%` }} />
      </div>
      <span className="cb-label" style={{ letterSpacing: "0.14em" }}>
        {ocupacao}/{capacidade} lugares
      </span>
    </div>
  );
}

/** Countdown until the 12h check-in window opens for a class on `isoDate` at `hora`. */
function windowCountdown(isoDate: string, hora: string, now: number): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  const [hh, mm] = hora.split(":").map(Number);
  if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return "";
  const classAt = new Date(y, m - 1, d, hh, mm, 0);
  return countdown(new Date(classAt.getTime() - 12 * 3600_000).toISOString(), now);
}

/** "hoje" / "amanhã" / "em 3 dias" / "2 dias atrás" */
function relativeDay(iso: string): string {
  const today = todayIso();
  if (iso === today) return "hoje";
  const diff = Math.round(
    (new Date(`${iso}T12:00:00`).getTime() - new Date(`${today}T12:00:00`).getTime()) / 86_400_000,
  );
  if (diff === 1) return "amanhã";
  if (diff === -1) return "ontem";
  return diff > 0 ? `em ${diff} dias` : `${Math.abs(diff)} dias atrás`;
}

export function TrainingTab({
  nextExecution,
  onOpenAutomation,
  onChanged,
}: {
  nextExecution: NextExecutionInfo | null;
  onOpenAutomation: () => void;
  onChanged: () => void;
}) {
  const [date, setDate] = useState(() => skipSunday(todayIso()));
  const [wod, setWod] = useState<WodResponse | null>(null);
  const [agenda, setAgenda] = useState<AgendaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<number | null>(null);
  const [notice, setNotice] = useState("");
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async (iso: string) => {
    setLoading(true);
    setError("");
    try {
      const apiDate = toApiDate(iso);
      const [wodRes, agendaRes] = await Promise.all([
        fetch(`/api/wod/${apiDate}`),
        fetch(`/api/agenda/${apiDate}`),
      ]);
      const wodJson = (await wodRes.json().catch(() => ({}))) as WodResponse & { error?: string };
      const agendaJson = (await agendaRes.json().catch(() => [])) as AgendaItem[] | { error?: string };
      if (!wodRes.ok) setError(wodJson.error ?? "Falha ao carregar o treino");
      else setWod(wodJson);
      if (agendaRes.ok) {
        setAgenda(Array.isArray(agendaJson) ? agendaJson : []);
      } else if (!Array.isArray(agendaJson) && agendaJson.error) {
        setError(agendaJson.error);
      }
    } catch {
      setError("Erro de rede ao consultar a academia");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(date);
  }, [date, load]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const clearNotice = useCallback(() => {
    const t = setTimeout(() => setNotice(""), 4000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => clearNotice(), [notice, clearNotice]);

  const post = async (url: string, body: unknown, okMessage: string) => {
    setNotice("");
    const res = await fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    const json = (await res.json().catch(() => ({}))) as { error?: string };
    if (!res.ok) {
      setNotice(json.error ?? "A academia recusou a operação");
      return false;
    }
    setNotice(okMessage);
    await load(date);
    onChanged();
    return true;
  };

  const toggleSubscription = async (item: AgendaItem) => {
    setBusy(item.idConfiguracao);
    try {
      if (item.status === 5) {
        await post(
          "/api/agenda/unsubscribe",
          { idConfiguracao: item.idConfiguracao, date: toApiDate(date) },
          `Cancelado: ${item.nome} ${item.horaInicio}`,
        );
      } else {
        await post(
          "/api/agenda/subscribe",
          { idConfiguracao: item.idConfiguracao, date: toApiDate(date) },
          `Check-in feito: ${item.nome} ${item.horaInicio}`,
        );
      }
    } finally {
      setBusy(null);
    }
  };

  const automateClass = async (item: AgendaItem) => {
    setBusy(item.idConfiguracao);
    try {
      const dayKey = String(new Date(`${date}T12:00:00`).getDay());
      await post(
        "/api/targets",
        { activity_name: item.nome, schedule: { [dayKey]: item.horaInicio } },
        `Automação definida: ${item.nome} ${item.horaInicio}`,
      );
    } finally {
      setBusy(null);
    }
  };

  const cancelOwnCheckin = async () => {
    if (!wod?.activity) return;
    setBusy(wod.activity.idConfiguracao);
    try {
      await post(
        "/api/wod/cancel",
        { idConfiguracao: wod.activity.idConfiguracao, date: toApiDate(date) },
        "Check-in cancelado",
      );
    } finally {
      setBusy(null);
    }
  };

  const isToday = date === todayIso();
  const active = wod?.activity ?? null;
  const blocks: Array<[string, string]> = wod?.wod
    ? [
        ["Aquecimento", wod.wod.warmup],
        ["Técnica", wod.wod.skill],
        ["WOD", wod.wod.workout],
      ]
    : [];

  return (
    <>
      {/* ── day toolbar ─────────────────────────────────────── */}
      <section className="cb-panel cb-rise">
        <div className="cb-panel__body" style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <div className="cb-row" style={{ gap: "0.35rem" }}>
            <button
              type="button"
              className="cb-btn cb-btn--ghost cb-btn--sm"
              onClick={() => setDate(shiftOpenDay(date, -1))}
              aria-label="Dia anterior"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              type="button"
              className={isToday ? "cb-btn cb-btn--sm" : "cb-btn cb-btn--sm cb-btn--primary"}
              onClick={() => setDate(skipSunday(todayIso()))}
            >
              Hoje
            </button>
            <button
              type="button"
              className="cb-btn cb-btn--ghost cb-btn--sm"
              onClick={() => setDate(shiftOpenDay(date, 1))}
              aria-label="Próximo dia"
            >
              <ChevronRight size={14} />
            </button>
          </div>

          <div style={{ minWidth: 0 }}>
            <p className="cb-display" style={{ fontSize: "1.6rem", margin: 0 }}>
              {weekdayShort(date)} <span style={{ color: isToday ? "var(--cb-accent)" : "var(--cb-text)" }}>{shortDate(date)}</span>
            </p>
            <p className="cb-label" style={{ marginTop: "0.1rem", color: isToday ? "var(--cb-accent)" : undefined }}>
              {relativeDay(date)}
            </p>
          </div>

          <div className="cb-spacer" />

          <DatePicker value={date} onChange={(v) => setDate(skipSunday(v))} className="w-[11rem]" id="cb-training-date" />

          <button
            type="button"
            className="cb-btn cb-btn--ghost cb-btn--sm"
            onClick={() => void load(date)}
            aria-label="Recarregar"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : undefined} />
          </button>
        </div>
        {(notice || error) && (
          <div
            className="cb-fade"
            style={{
              padding: "0.5rem 1.1rem",
              borderTop: "1px solid var(--cb-border)",
              fontFamily: "var(--font-mono)",
              fontSize: "0.72rem",
              color: error ? "var(--cb-error)" : "var(--cb-accent)",
            }}
          >
            {error || notice}
          </div>
        )}
      </section>

      <div className="cb-grid-2">
        {/* ── left column: board + next shot ────────────────── */}
        <div style={{ display: "grid", gap: "0.85rem", alignContent: "start" }}>
          <section className="cb-board cb-rise-1">
            <div className="cb-board__head">
              <span className="cb-panel__title" style={{ color: "var(--cb-text)" }}>
                <Zap size={13} style={{ color: "var(--cb-accent)" }} />
                Treino do dia
              </span>
              <div className="cb-row" style={{ gap: "0.4rem" }}>
                {active && (
                  <>
                    <span className="cb-chip cb-chip--accent">
                      <Clock size={11} />
                      {active.horaInicio}
                    </span>
                    <span className="cb-chip">{active.nome}</span>
                  </>
                )}
              </div>
            </div>

            {loading && !wod ? (
              <div className="cb-empty">carregando treino…</div>
            ) : blocks.length === 0 ? (
              <div className="cb-empty">
                <strong>WOD ainda não publicado</strong>
                <span>o treino costuma sair algumas horas antes da aula</span>
              </div>
            ) : (
              blocks.map(([label, content], i) =>
                content ? (
                  <div className="cb-board__block cb-rise" style={{ animationDelay: `${80 * (i + 1)}ms` }} key={label}>
                    <div className="cb-row" style={{ marginBottom: "0.5rem" }}>
                      <span className="cb-label" style={{ color: "var(--cb-accent)" }}>{label}</span>
                      <span style={{ flex: 1, height: 1, background: "var(--cb-border2)" }} />
                    </div>
                    <p className="cb-workout-text" style={{ margin: 0 }}>{content}</p>
                  </div>
                ) : null,
              )
            )}
          </section>

          <section className="cb-panel cb-rise-2">
            <div className="cb-panel__head">
              <span className="cb-panel__title">
                <Timer size={13} />
                Próximo disparo automático
              </span>
              <button type="button" className="cb-btn cb-btn--sm" onClick={onOpenAutomation}>
                Configurar
              </button>
            </div>
            <div className="cb-panel__body">
              {nextExecution ? (
                <div className="cb-row" style={{ gap: "1.5rem", alignItems: "flex-start", flexWrap: "wrap" }}>
                  <div>
                    <p className="cb-display" style={{ fontSize: "2.6rem", color: "var(--cb-accent)", margin: 0 }}>
                      {countdown(nextExecution.type === "prep" ? nextExecution.prepAt : nextExecution.windowOpensAt, now)}
                    </p>
                    <p className="cb-label">
                      {nextExecution.type === "prep" ? "preparação" : "execução"} · {nextExecution.time}{" "}
                      {nextExecution.activityName}
                    </p>
                  </div>
                  <div style={{ flex: 1, minWidth: "14rem" }}>
                    <div className="cb-kv">
                      <span className="cb-kv__k">Aula</span>
                      <span className="cb-kv__v">{displayDate(nextExecution.targetDate)}</span>
                    </div>
                    <div className="cb-kv">
                      <span className="cb-kv__k">Janela abre</span>
                      <span className="cb-kv__v">{clockTime(nextExecution.windowOpensAt)}</span>
                    </div>
                    <div className="cb-kv">
                      <span className="cb-kv__k">Preparo</span>
                      <span className="cb-kv__v">{clockTime(nextExecution.prepAt)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="cb-empty">
                  <strong>Sem automação agendada</strong>
                  <span>defina a atividade e os dias na aba Automação para o check-in disparar sozinho</span>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* ── right column: current class ───────────────────── */}
        <div style={{ display: "grid", gap: "0.85rem", alignContent: "start", position: "sticky", top: "4.6rem" }}>
          <section className="cb-panel cb-rise-2">
            <div className="cb-panel__head">
              <span className="cb-panel__title">
                <Users size={13} />
                Sua aula
              </span>
              {active && <AgendaPill status={active.status} ocupacao={active.ocupacao} capacidade={active.capacidade} />}
            </div>
            <div className="cb-panel__body" style={{ display: "grid", gap: "0.9rem" }}>
              {active ? (
                <>
                  <div className="cb-row" style={{ alignItems: "flex-end", gap: "0.75rem" }}>
                    <span className="cb-display cb-hero-time" style={{ color: "var(--cb-accent)" }}>
                      {active.horaInicio}
                    </span>
                    <div style={{ paddingBottom: "0.35rem" }}>
                      <p className="cb-display" style={{ fontSize: "1.35rem", margin: 0 }}>{active.nome}</p>
                      <p className="cb-label">{active.selecionado ? "check-in confirmado" : "reserva pendente"}</p>
                    </div>
                  </div>
                  <OccupancyMeter ocupacao={active.ocupacao} capacidade={active.capacidade} />
                  {wod && wod.participants.length > 0 && (
                    <div>
                      <p className="cb-label" style={{ marginBottom: "0.4rem" }}>na turma</p>
                      <div className="cb-row" style={{ gap: "0.3rem" }}>
                        {wod.participants.slice(0, 12).map((p) => (
                          <span className="cb-chip cb-chip--ghost" key={p.idCliente}>
                            {p.oculto ? "aluno" : p.nome.split(" ")[0]}
                          </span>
                        ))}
                        {wod.participants.length > 12 && (
                          <span className="cb-chip cb-chip--ghost">+{wod.participants.length - 12}</span>
                        )}
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    className="cb-btn cb-btn--danger"
                    disabled={busy === active.idConfiguracao}
                    onClick={() => void cancelOwnCheckin()}
                  >
                    <CircleSlash2 size={13} />
                    Cancelar check-in
                  </button>
                </>
              ) : (
                <div className="cb-empty">
                  <strong>Nenhuma aula reservada neste dia</strong>
                  <span>faça o check-in na agenda abaixo ou automatize</span>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* ── agenda ──────────────────────────────────────────── */}
      <section className="cb-panel cb-rise-4">
        <div className="cb-panel__head">
          <span className="cb-panel__title">
            <CalendarDays size={13} />
            Agenda · {shortDate(date)}
          </span>
          <span className="cb-label">{agenda.length} aulas</span>
        </div>
        {loading && agenda.length === 0 ? (
          <div className="cb-empty">consultando agenda…</div>
        ) : agenda.length === 0 ? (
          <div className="cb-empty">nenhuma aula publicada neste dia</div>
        ) : (
          agenda.map((item) => {
            const windowIn = item.status === 9 ? windowCountdown(date, item.horaInicio, now) : "";
            const full = item.capacidade > 0 && item.ocupacao >= item.capacidade;
            const canCheckin = item.status === 1 && !full;
            const isTarget =
              nextExecution !== null &&
              nextExecution.time === item.horaInicio &&
              nextExecution.targetDate === toApiDate(date);
            return (
              <div
                className="cb-class"
                key={item.idConfiguracao}
                style={isTarget ? { boxShadow: "inset 2px 0 0 var(--cb-accent)" } : undefined}
              >
                <span className="cb-class__time">{item.horaInicio}</span>
                <div style={{ minWidth: 0, display: "grid", gap: "0.35rem" }}>
                  <div className="cb-row" style={{ gap: "0.45rem" }}>
                    <span style={{ fontWeight: 500 }}>{item.nome}</span>
                    {isTarget && <span className="cb-pill" style={{ color: "var(--cb-accent)", background: "var(--cb-accent-dim)", borderColor: "var(--cb-accent-border)" }}>alvo</span>}
                    {item.status !== 9 && (
                      <AgendaPill status={item.status} ocupacao={item.ocupacao} capacidade={item.capacidade} />
                    )}
                  </div>
                  <OccupancyMeter ocupacao={item.ocupacao} capacidade={item.capacidade} />
                </div>
                <div className="cb-row" style={{ gap: "0.35rem", justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    className="cb-btn cb-btn--ghost cb-btn--sm"
                    disabled={busy === item.idConfiguracao}
                    onClick={() => void automateClass(item)}
                    title="Usar como regra de check-in automático"
                  >
                    <Zap size={12} />
                  </button>
                  {item.status === 5 ? (
                    <button
                      type="button"
                      className="cb-btn cb-btn--sm cb-btn--danger"
                      disabled={busy === item.idConfiguracao}
                      onClick={() => void toggleSubscription(item)}
                    >
                      Cancelar
                    </button>
                  ) : canCheckin ? (
                    <button
                      type="button"
                      className="cb-btn cb-btn--sm cb-btn--primary"
                      disabled={busy === item.idConfiguracao}
                      onClick={() => void toggleSubscription(item)}
                    >
                      Check-in
                    </button>
                  ) : (
                    <button type="button" className="cb-btn cb-btn--sm" disabled>
                      {full ? "Lotada" : windowIn ? `Abre em ${windowIn}` : "Indisponível"}
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </section>
    </>
  );
}
